import Base from '@studiometa/js-toolkit/abstracts/Base';
import withBreakpointObserver from '@studiometa/js-toolkit/decorators/withBreakpointObserver';
import { isDev, MENU_BURGER_BREAKPOINTS } from '../../config';
import ItemFirstLevel from './ItemFirstLevel';

/**
 * MenuMain class.
 */
export default class MenuMain extends withBreakpointObserver(Base) {
  /**
   * Class config.
   * @return {Object}
   */
  get config() {
    return {
      name: 'MenuMain',
      log: isDev(),
      inactiveBreakpoints: MENU_BURGER_BREAKPOINTS,
      components: {
        ItemFirstLevel,
      },
    };
  }

  /**
   * Orchestrate each menu items with the others.
   * @return {void}
   */
  mounted() {
    const unbindMethods = [];

    if (this.$parent) {
      unbindMethods.push(this.$parent.$on('hide', this.closeCurrentSubmenu));
    }

    if (this.$children.ItemFirstLevel) {
      this.unbindMethods = [
        ...unbindMethods,
        ...this.$children.ItemFirstLevel.map((item, index) => [
          item.$on('closeSubmenu', () => this.onItemsCloseSubmenu(index)),
          item.$on('openSubmenu', () => this.onItemsOpenSubmenu(index)),
        ]),
      ].flat();
    } else {
      this.unbindMethods = [...unbindMethods].flat();
    }
  }

  /**
   * Unbind every bounded functions.
   */
  destroyed() {
    this.unbindMethods.forEach((unbindMethod) => unbindMethod());
  }

  /**
   * Close other menu when focus is set on a item.
   * @param {Number} index The item position.
   */
  onItemFirstLevelFocusin(index) {
    this.$refs.items
      .filter((otherItem, i) => i !== index && otherItem.submenuIsOpen)
      .forEach((otherItem) => otherItem.closeSubmenu());
  }

  /**
   * Proxy up the `closeSubmenu` event.
   * @param {Number} index The current submenu index.
   */
  onItemsCloseSubmenu(index) {
    this.$emit('closeSubmenu', this.$refs.items[index], index);
  }

  /**
   * Proxy up the `openSubmenu` event and sync submenus opening.
   * @param {Number} index The current submenu index.
   */
  onItemsOpenSubmenu(index) {
    this.$emit('openSubmenu', this.$refs.items[index], index);
    this.$refs.items
      .filter((otherItem, i) => i !== index && otherItem.hasSubmenu)
      .forEach((otherItem) => otherItem.closeSubmenu());
  }

  /**
   * Close the currently opened submenu and save it.
   * @return {void}
   */
  closeCurrentSubmenu() {
    const currentSubmenu = this.$refs.items.find((item) => item.submenuIsOpen);

    if (!currentSubmenu) {
      return;
    }

    currentSubmenu.closeSubmenu();
  }
}
